import { Button, Dialog, DialogContent, DialogTitle, Divider, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { useState } from "react";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import type { ApproverAssignment } from "../../../api/types/approvalTypes";
import ApprovalAssignmentEditor from "../ApprovalAssignmentEditor";
import type { ApproversAssignmentState } from "./approversAssignmentState";
import {
  changeApproversAssignmentUserSelectionAction,
  getApproversAssignmentStateValidationErrors,
  mapStateToApproverAssignments,
} from "./approversAssignmentState";
import NoApproversWarningDialog from "./NoApproversWarningDialog";

interface Props {
  initialState: ApproversAssignmentState;
  actionName: string;
  confirmColor?: "primary" | "error";
  loading: boolean;
  disabled?: boolean;
  Subheader?: ReactNode;
  children?: ReactNode;
  onConfirm: (approverAssignments: ApproverAssignment[]) => void;
  onClose: () => void;
}

const ApproversAssignmentDialog = ({
  initialState,
  actionName,
  confirmColor = "primary",
  loading,
  disabled,
  Subheader,
  children,
  onConfirm,
  onClose,
}: Props) => {
  const [assignmentState, setAssignmentState] = useState(initialState);

  const handleSelectionChange = (stepName: string, ruleIndex: number, userId: string) => {
    setAssignmentState(changeApproversAssignmentUserSelectionAction(stepName, ruleIndex, userId));
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (assignmentState.steps.length === 0) {
    return <NoApproversWarningDialog actionName={actionName} onClose={onClose} />;
  }

  const validationErrors = getApproversAssignmentStateValidationErrors(assignmentState);

  return (
    <Dialog open onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Select Approvers</DialogTitle>
      <DialogCloseButton onClick={handleClose} />
      <Divider />
      <DialogContent sx={{ overflowY: "hidden" }}>
        <Typography color="textSecondary" sx={{ textAlign: "center" }}>
          Select members for the approval process. Each rule must include at least one approver.
        </Typography>

        {Subheader}

        <ApprovalAssignmentEditor
          steps={assignmentState.steps}
          validationErrors={validationErrors}
          onSelectionChange={handleSelectionChange}
          containerProps={{ sx: { pt: 2, maxHeight: "70vh", overflowY: "auto" } }}
        />

        {children}
      </DialogContent>
      <Divider />
      <DialogButtons onCancel={handleClose} cancelDisabled={loading}>
        <Button
          color={confirmColor}
          variant={confirmColor === "error" ? "outlined" : "contained"}
          loading={loading}
          disabled={disabled || validationErrors.length > 0}
          onClick={() => onConfirm(mapStateToApproverAssignments(assignmentState))}
        >
          {actionName}
        </Button>
      </DialogButtons>
    </Dialog>
  );
};

export default ApproversAssignmentDialog;
